import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, MenuItem, Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend
);

// Dark theme colors
const DARK_COLORS = {
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  ERROR: "#ff4444",
  WARNING: "#ff9800",
  INFO: "#2196f3"
};

interface ChartModalProps {
  open: boolean;
  onClose: () => void;
  headers: string[];
  rows: Record<string, any>[];
  x_axis: string;
  y_axis: string | string[];
  chart_type: 'bar' | 'line' | 'pie';
}

const ChartModal: React.FC<ChartModalProps> = ({
  open,
  onClose,
  headers,
  rows,
  x_axis,
  y_axis,
  chart_type,
}) => {
  const [currentType, setCurrentType] = useState<'bar' | 'line' | 'pie'>(chart_type);
  
  // Dark theme chart colors
  const darkPalette = [
    '#2196f3', // Blue
    '#4caf50', // Green  
    '#ff9800', // Orange
    '#9c27b0', // Purple
    '#f44336', // Red
    '#00bcd4', // Cyan
    '#ffeb3b', // Yellow
    '#795548', // Brown
  ];

  const normalizeValue = (value: any): number => {
    if (typeof value === 'string') {
      const cleaned = value.replace(/[€\s]/g, '').replace(',', '.');
      return parseFloat(cleaned) || 0;
    }
    return typeof value === 'number' ? value : 0;
  };

  if (!headers.length || !rows.length || !x_axis || !y_axis.length)
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: DARK_COLORS.SURFACE,
            border: `1px solid ${DARK_COLORS.BORDER}`,
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: DARK_COLORS.TEXT_PRIMARY }}>
          ⚠️ Non è possibile creare un grafico
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: DARK_COLORS.TEXT_SECONDARY }}>
            I dati non contengono colonne utili per il grafico.
          </Typography>
        </DialogContent>
      </Dialog>
    );

  const yFields = Array.isArray(y_axis) ? y_axis : [y_axis];

  const datasets =
    currentType === 'pie'
      ? []
      : yFields.map((field, index) => ({
          label: field,
          data: rows.map((r) => normalizeValue(r[field]) || 0),
          backgroundColor:
            currentType === 'bar'
              ? darkPalette[index % darkPalette.length]
              : `${darkPalette[index % darkPalette.length]}40`, // Semi-transparent for area
          borderColor: darkPalette[index % darkPalette.length],
          borderWidth: currentType === 'line' ? 3 : 1,
          tension: currentType === 'line' ? 0.4 : 0,
          fill: currentType === 'line' ? true : false,
        }));

  const ChartComponent =
    currentType === 'bar' ? Bar : currentType === 'line' ? Line : Pie;

  const chartData = {
    labels: rows.map((r) => String(r[x_axis] ?? '–')),
    datasets:
      currentType === 'pie'
        ? [
            {
              label: yFields[0],
              data: rows.map((r) => normalizeValue(r[yFields[0]]) || 0),
              backgroundColor: rows.map(
                (_, i) => darkPalette[i % darkPalette.length]
              ),
              borderColor: DARK_COLORS.BORDER,
              borderWidth: 2,
              hoverBorderWidth: 3,
              hoverBorderColor: DARK_COLORS.TEXT_PRIMARY,
            },
          ]
        : datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        backgroundColor: DARK_COLORS.SURFACE,
        titleColor: DARK_COLORS.TEXT_PRIMARY,
        bodyColor: DARK_COLORS.TEXT_SECONDARY,
        borderColor: DARK_COLORS.BORDER,
        borderWidth: 1,
        titleFont: { size: 14, weight: 700 },
        bodyFont: { size: 13 },
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
      },
      legend: {
        display: currentType === 'pie' || yFields.length > 1,
        position: 'bottom' as const,
        labels: {
          color: DARK_COLORS.TEXT_PRIMARY,
          font: { size: 12, weight: 500 },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
    },
    scales:
      currentType !== 'pie'
        ? {
            x: {
              ticks: {
                color: DARK_COLORS.TEXT_SECONDARY,
                font: { size: 12, weight: 500 },
              },
              grid: { 
                display: false,
                color: DARK_COLORS.BORDER,
              },
              border: {
                color: DARK_COLORS.BORDER,
              },
            },
            y: {
              ticks: {
                color: DARK_COLORS.TEXT_SECONDARY,
                font: { size: 12, weight: 500 },
              },
              grid: { 
                color: `${DARK_COLORS.BORDER}80`, // Semi-transparent
                lineWidth: 1,
              },
              border: {
                color: DARK_COLORS.BORDER,
              },
            },
          }
        : {},
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundColor: DARK_COLORS.SURFACE,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.9)',
          border: `1px solid ${DARK_COLORS.BORDER}`,
          height: '80vh',
          maxHeight: '800px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 1.5,
          backgroundColor: DARK_COLORS.ACCENT,
          borderBottom: `1px solid ${DARK_COLORS.BORDER}`,
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: DARK_COLORS.TEXT_PRIMARY,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          📊 Grafico: <Box component="span" sx={{ color: DARK_COLORS.INFO }}>{yFields.join(' + ')}</Box> rispetto a <Box component="span" sx={{ color: DARK_COLORS.SUCCESS }}>{x_axis}</Box>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Select
            size="small"
            value={currentType}
            onChange={(e) =>
              setCurrentType(e.target.value as 'bar' | 'line' | 'pie')
            }
            sx={{ 
              minWidth: 100,
              backgroundColor: DARK_COLORS.ACCENT,
              color: DARK_COLORS.TEXT_PRIMARY,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: DARK_COLORS.BORDER,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: DARK_COLORS.TEXT_SECONDARY,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: DARK_COLORS.INFO,
              },
              '& .MuiSvgIcon-root': {
                color: DARK_COLORS.TEXT_SECONDARY,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: DARK_COLORS.SURFACE,
                  border: `1px solid ${DARK_COLORS.BORDER}`,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
                  '& .MuiMenuItem-root': {
                    color: DARK_COLORS.TEXT_PRIMARY,
                    '&:hover': {
                      backgroundColor: DARK_COLORS.HOVER,
                    },
                    '&.Mui-selected': {
                      backgroundColor: DARK_COLORS.ACCENT,
                      '&:hover': {
                        backgroundColor: DARK_COLORS.HOVER,
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="bar">📊 Bar Chart</MenuItem>
            <MenuItem value="line">📈 Line Chart</MenuItem>
            <MenuItem value="pie">🥧 Pie Chart</MenuItem>
          </Select>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              color: DARK_COLORS.TEXT_SECONDARY,
              '&:hover': {
                color: DARK_COLORS.TEXT_PRIMARY,
                backgroundColor: DARK_COLORS.HOVER,
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          p: 3,
          backgroundColor: DARK_COLORS.SURFACE,
          height: '100%',
        }}
      >
        <Box 
          sx={{ 
            height: '100%', 
            minHeight: 400,
            backgroundColor: DARK_COLORS.ACCENT,
            borderRadius: 2,
            p: 2,
            border: `1px solid ${DARK_COLORS.BORDER}`,
            boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.3)',
          }}
        >
          <ChartComponent data={chartData} options={options} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChartModal;